<script lang="ts" module>
	export interface Picked {
		file: File;
		/** An object URL of the file, or null when it is not a picture. */
		url: string | null;
		problem: null | 'type' | 'size' | 'small';
	}
</script>

<script lang="ts">
	// The design system's ImageInput (design/components/surfaces/ImageInput.jsx):
	// one image picked from the device, as the restaurant's logo (UC-32, D16)
	// or an item's photo (UC-9.R16, D8). A square frame shows the image, or
	// that there is none, and beside it the actions: Add or Replace, and
	// Remove. Dropping a file on the frame does what Add does; the button is
	// always the way, the drop a shortcut.
	//
	// It checks what it can before anything is sent, the type, the bytes and
	// the pixel size, and hands the page { file, url, problem }, where problem
	// is null, "type", "size" or "small". The page words the problem and
	// passes it back as error. The server's IMAGE_INVALID uses the same three
	// words, so a file that slips past the check lands in the same message.
	// The image is contained, never cropped by the frame. What the server
	// keeps depends on the use: a logo it fits into 512 by 512 pixels and
	// crops nothing; an item photo it centre-crops square and fits within
	// 1024 by 1024, and D8 shows the square it will send
	// (panels/shrinkPhoto.ts), so the frame shows what is kept.

	import Button from './Button.svelte';

	interface Props {
		testId: string;
		src?: string;
		alt?: string;
		emptyLabel: string;
		addLabel: string;
		replaceLabel: string;
		removeLabel: string;
		onPick: (picked: Picked) => void;
		onRemove?: () => void;
		/** Remove is off; note says why, so it is never silently off. */
		removeDisabled?: boolean;
		state?: 'idle' | 'pending';
		pendingLabel?: string;
		error?: string;
		note?: string;
		accept?: string;
		maxBytes?: number;
		minPx?: number;
		disabled?: boolean;
	}

	let {
		testId,
		src,
		alt = '',
		emptyLabel,
		addLabel,
		replaceLabel,
		removeLabel,
		onPick,
		onRemove,
		removeDisabled = false,
		state: phase = 'idle',
		pendingLabel,
		error,
		note,
		accept = 'image/png,image/jpeg',
		maxBytes = 2 * 1024 * 1024,
		minPx = 64,
		disabled = false,
	}: Props = $props();

	let input = $state<HTMLInputElement>();
	let over = $state(false);
	const busy = $derived(phase === 'pending');
	const off = $derived(disabled || busy);

	function take(file: File | undefined) {
		if (!file) return;
		const types = accept.split(',').map((t) => t.trim());
		if (!types.includes(file.type)) {
			onPick({ file, url: null, problem: 'type' });
			return;
		}
		if (file.size > maxBytes) {
			onPick({ file, url: null, problem: 'size' });
			return;
		}
		const url = URL.createObjectURL(file);
		const img = new Image();
		img.onload = () =>
			onPick({
				file,
				url,
				problem: img.naturalWidth < minPx || img.naturalHeight < minPx ? 'small' : null,
			});
		img.onerror = () => onPick({ file, url: null, problem: 'type' });
		img.src = url;
	}
</script>

<div class="image-input" data-testid={testId}>
	<div
		class="frame"
		class:filled={!!src}
		class:over
		class:invalid={!!error}
		role="presentation"
		ondragover={(event) => {
			if (off) return;
			event.preventDefault();
			over = true;
		}}
		ondragleave={() => (over = false)}
		ondrop={(event) => {
			event.preventDefault();
			over = false;
			if (!off) take(event.dataTransfer?.files?.[0]);
		}}
	>
		{#if src}
			<img {src} {alt} data-testid="{testId}-image" />
		{:else}
			<span class="empty">{emptyLabel}</span>
		{/if}
	</div>
	<div class="side">
		<div class="actions">
			<Button
				testId="{testId}-pick"
				tone="secondary"
				size="sm"
				label={src ? replaceLabel : addLabel}
				state={busy ? 'pending' : 'idle'}
				{pendingLabel}
				{disabled}
				onPress={() => input?.click()}
			/>
			{#if src && onRemove}
				<Button
					testId="{testId}-remove"
					tone="text-quiet"
					size="sm"
					label={removeLabel}
					disabled={off || removeDisabled}
					onPress={onRemove}
				/>
			{/if}
		</div>
		{#if error}
			<p class="error" role="alert" data-testid="{testId}-error">{error}</p>
		{:else if note}
			<p class="note" data-testid="{testId}-note">{note}</p>
		{/if}
	</div>
	<input
		bind:this={input}
		type="file"
		{accept}
		hidden
		data-testid="{testId}-file"
		onchange={(event) => {
			const target = event.currentTarget as HTMLInputElement;
			const file = target.files?.[0];
			target.value = '';
			take(file);
		}}
	/>
</div>

<style>
	.image-input {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-7);
	}

	.frame {
		inline-size: var(--logo-lg);
		block-size: var(--logo-lg);
		flex: none;
		display: grid;
		place-items: center;
		overflow: hidden;
		box-sizing: border-box;
		background: var(--surface-well);
		border-radius: var(--radius-tile);
		border: var(--border-width-strong) dashed var(--border-default);
	}

	.frame.filled {
		border-style: solid;
	}

	.frame.invalid {
		border-color: var(--ink-flag);
	}

	.frame.over {
		border-color: var(--text-strong);
	}

	.frame img {
		inline-size: 100%;
		block-size: 100%;
		object-fit: contain;
		display: block;
	}

	.empty {
		padding: var(--space-3);
		font-size: var(--text-micro);
		line-height: 1.4;
		color: var(--text-body);
		text-align: center;
	}

	.side {
		flex: 1 1 14em;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-3);
	}

	.actions {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-5);
	}

	.error,
	.note {
		margin: 0;
		font-size: 13px;
		line-height: 1.7;
	}

	.error {
		font-weight: 600;
		color: var(--ink-flag-deep);
	}

	.note {
		color: var(--text-body);
	}
</style>
